import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PatientPropChange } from "../../../../../@types/Patient";
import { ArrowForward } from "../../../../../assets/icons/ArrowForward";
import { Button } from "../../../../../components/Button";
import { Checkbox } from "../../../../../components/Checkbox";
import { getWardLabel } from "../../../../../utils/wardLabel";
import { CancelButton } from "../../PatientAlertsTable/components/ManageTriggersModal/ManageTriggersModal.styles";
import { getCardLabelKey } from "../cardsConfigs";
import {
  PropsChangesContainer,
  Container,
  PropContainer,
  Subtitle,
  Title,
  PropTitle,
  Content,
  SubmitContainer,
  SendEnrolmentEmailContainer,
  SendEnrolmentEmailText,
} from "./ConfirmChangesModal.styles";

type ConfirmChangesProps = {
  propsChanged: PatientPropChange[];
  onClose: () => void;
  onSave: (onlyCardsOrder: boolean, sendEnrolmentEmail: boolean) => void;
};

export function ConfirmChangesModal({
  propsChanged,
  onClose,
  onSave,
}: ConfirmChangesProps) {
  const { t } = useTranslation();

  const [sendEnrolmentEmail, setSendEnrolmentEmail] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // `propName` is the internal card key (e.g. "Ward") that the save logic keys
  // off — keep it untouched and only swap the displayed title for the hospital's
  // configured ward label.
  const wardLabel = getWardLabel();
  const getPropDisplayName = (propName: string): string => {
    if (propName === "Ward") return wardLabel.singular;
    const labelKey = getCardLabelKey(propName);
    return labelKey ? t(labelKey) : propName;
  };

  const checkEmailChange = (): boolean => {
    const emailChange = propsChanged.find(
      (change) => change.propName === "Email"
    );

    if (emailChange === undefined) return false;

    return true;
  };

  const handleOnSave = () => {
    setIsLoading(true);

    if (propsChanged.length === 0) {
      onSave(true, false);
    } else {
      onSave(false, sendEnrolmentEmail);
    }
  };

  if (propsChanged.length === 0) {
    return (
      <Container>
        <Title>{t("patientPage_confirmChanges_title")}</Title>
        <Subtitle larger>{t("patientPage_alertsConfig_confirmBody")}</Subtitle>
        <SubmitContainer>
          <Button
            label={t("patientPage_confirmChanges_confirm")}
            type="submit"
            isLoading={isLoading}
            onClick={handleOnSave}
          />
          <CancelButton onClick={onClose} style={{ marginLeft: "10px" }}>
            {t("common_cancel")}
          </CancelButton>
        </SubmitContainer>
      </Container>
    );
  }

  const handlePropsToDisplay = () => {
    const propsToDisplay: any[] = [];

    propsChanged.forEach((prop) => {
      propsToDisplay.push(
        <PropContainer key={`prop-container-${prop.propName}`}>
          <PropTitle>
            {prop.displayName ?? getPropDisplayName(prop.propName)}
          </PropTitle>
          <Content>
            {prop.oldValue !== "-" ? (
              <>
                <p>{prop.oldValue}</p>
                <div
                  style={{
                    margin: "0 15px",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <ArrowForward />
                </div>
              </>
            ) : null}
            <p>{prop.newValue}</p>
          </Content>
        </PropContainer>
      );
    });

    return propsToDisplay;
  };

  return (
    <Container>
      <Title>{t("patientPage_confirmChanges_title")}</Title>
      <Subtitle>{t("patientPage_confirmChanges_body")}</Subtitle>

      <PropsChangesContainer>{handlePropsToDisplay()}</PropsChangesContainer>
      {checkEmailChange() && (
        <SendEnrolmentEmailContainer>
          <Checkbox
            label=""
            checked={sendEnrolmentEmail}
            onChange={(isChecked) => {
              setSendEnrolmentEmail(isChecked);
            }}
          />
          <SendEnrolmentEmailText highlighted>
            {t("patientPage_confirmChanges_sendEnrolmentEmail")}
          </SendEnrolmentEmailText>
          &nbsp;
          <SendEnrolmentEmailText>
            {t("patientPage_confirmChanges_toNewAddress")}
          </SendEnrolmentEmailText>
        </SendEnrolmentEmailContainer>
      )}
      <SubmitContainer>
        <Button
          label={t("patientPage_confirmChanges_confirm")}
          type="button"
          isLoading={isLoading}
          onClick={handleOnSave}
        />

        <CancelButton onClick={onClose} style={{ marginLeft: "10px" }}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
